import {
  Input as BaseInput,
  Box,
  Button,
  CircularProgress,
  styled,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import * as React from "react";
import { PrimaryButton } from "../../../components/ui/button";
import theme from "../../../theme";
import { useTranslation } from "react-i18next";

function OTP({ separator, length, value, onChange }) {
  const inputRefs = React.useRef(new Array(length).fill(null));

  const focusInput = (targetIndex) => {
    const targetInput = inputRefs.current[targetIndex];
    targetInput.focus();
  };

  const selectInput = (targetIndex) => {
    const targetInput = inputRefs.current[targetIndex];
    targetInput.select();
  };

  const handleKeyDown = (event, currentIndex) => {
    switch (event.key) {
      case "ArrowUp":
      case "ArrowDown":
      case " ":
        event.preventDefault();
        break;
      case "ArrowLeft":
        event.preventDefault();
        if (currentIndex > 0) {
          focusInput(currentIndex - 1);
          selectInput(currentIndex - 1);
        }
        break;
      case "ArrowRight":
        event.preventDefault();
        if (currentIndex < length - 1) {
          focusInput(currentIndex + 1);
          selectInput(currentIndex + 1);
        }
        break;
      case "Delete":
        event.preventDefault();
        onChange((prevOtp) => {
          const otp =
            prevOtp.slice(0, currentIndex) + prevOtp.slice(currentIndex + 1);
          return otp;
        });

        break;
      case "Backspace":
        event.preventDefault();
        if (currentIndex > 0) {
          focusInput(currentIndex - 1);
          selectInput(currentIndex - 1);
        }

        onChange((prevOtp) => {
          const otp =
            prevOtp.slice(0, currentIndex) + prevOtp.slice(currentIndex + 1);
          return otp;
        });
        break;

      default:
        break;
    }
  };

  const handleChange = (event, currentIndex) => {
    const currentValue = event.target.value;
    let indexToEnter = 0;

    while (indexToEnter <= currentIndex) {
      if (
        inputRefs.current[indexToEnter].value &&
        indexToEnter < currentIndex
      ) {
        indexToEnter += 1;
      } else {
        break;
      }
    }
    onChange((prev) => {
      const otpArray = prev.split("");
      const lastValue = currentValue[currentValue.length - 1];
      otpArray[indexToEnter] = lastValue;
      return otpArray.join("");
    });
    if (currentValue !== "") {
      if (currentIndex < length - 1) {
        focusInput(currentIndex + 1);
      }
    }
  };

  const handleClick = (event, currentIndex) => {
    selectInput(currentIndex);
  };

  const handlePaste = (event, currentIndex) => {
    event.preventDefault();
    const clipboardData = event.clipboardData;

    // Check if there is text data in the clipboard
    if (clipboardData.types.includes("text/plain")) {
      let pastedText = clipboardData.getData("text/plain");
      pastedText = pastedText.substring(0, length).trim();
      let indexToEnter = 0;

      while (indexToEnter <= currentIndex) {
        if (
          inputRefs.current[indexToEnter].value &&
          indexToEnter < currentIndex
        ) {
          indexToEnter += 1;
        } else {
          break;
        }
      }

      const otpArray = value.split("");

      for (let i = indexToEnter; i < length; i += 1) {
        const lastValue = pastedText[i - indexToEnter] ?? " ";
        otpArray[i] = lastValue;
      }

      onChange(otpArray.join(""));
    }
  };

  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
      {new Array(length).fill(null).map((_, index) => (
        <React.Fragment key={index}>
          <BaseInput
            slots={{
              input: InputElement,
            }}
            type="number"
            aria-label={`Digit ${index + 1} of OTP`}
            inputRef={(ele) => {
              inputRefs.current[index] = ele;
            }}
            onKeyDown={(event) => handleKeyDown(event, index)}
            onChange={(event) => handleChange(event, index)}
            onClick={(event) => handleClick(event, index)}
            onPaste={handlePaste}
            value={value[index] ?? ""}
            sx={{
              "& input": {
                "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
                  "-webkit-appearance": "none",
                  margin: 0,
                },
                "&[type=number]": {
                  "-moz-appearance": "textfield",
                },
                backgroundColor: value[index] ? "primary.main" : "inherit", // Blue background when filled
                color: value[index] ? "text.secondary" : "inherit", // White text when filled
                // Add transition for smooth color change
                transition: "background-color 0.3s, color 0.3s",
                // You can add more styling here
                textAlign: "center",
                fontSize: "1.2rem",
                fontWeight: "bold",
                // Ensure the input looks good with the new colors
                "&:hover": {
                  backgroundColor: value[index]
                    ? "#1565c0"
                    : "rgba(0, 0, 0, 0.04)",
                },
                "&:focus": {
                  backgroundColor: value[index] ? "#1565c0" : "white",
                },
              },
            }}
          />
          {index === length - 1 ? null : separator}
        </React.Fragment>
      ))}
    </Box>
  );
}

OTP.propTypes = {
  length: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  separator: PropTypes.node,
  value: PropTypes.string.isRequired,
};

export default function OTPInput({
  phoneNumber,
  onVerify,
  onResend,
  isSubmitting,
}) {
  const { t } = useTranslation("auth");

  const [otp, setOtp] = React.useState("");
  const [timeLeft, setTimeLeft] = React.useState(30);
  const [error, setError] = React.useState("");

  // Resend OTP handler
  const handleResend = () => {
    if (onResend) {
      onResend();
    }
    setTimeLeft(30);
    setError("");
  };

  // Timer effect
  React.useEffect(() => {
    const timer =
      timeLeft > 0 && setInterval(() => setTimeLeft(timeLeft - 1), 1000);
    return () => clearInterval(timer);
  }, [timeLeft]);

  // Handle OTP verification
  const handleVerify = () => {
    if (otp.length !== 6) {
      setError(t("otp_error_invalid_length")); // <--- Use new translation key here
      return;
    }

    // Clear any previous errors
    setError("");

    // Call verification handler
    if (onVerify) {
      console.log(`onVerify`);
      onVerify(otp);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 10,
        justifyContent: "center",
        alignItems: "center",
        mx: "auto",
      }}
    >
      <Box display={"flex"} flexDirection={"column"} gap={2}
        sx={{
          marginTop: "2rem"
        }}
      >
        <Typography
          fontWeight={400}
          fontSize={"20px"}
          color="text.primary"
          sx={{ textAlign: "center" }}
        >
          {t("enter_the_verification_code")}
        </Typography>
        <OTP
          separator={<span> </span>}
          value={otp}
          onChange={setOtp}
          length={6}
        />
        {error && (
          <Typography color="error" variant="body2" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}
      </Box>
      <Box display={"flex"} flexDirection={"column"} gap={2} width={"100%"}>
        <PrimaryButton
          fullWidth
          variant="contained"
          color="primary"
          onClick={handleVerify}
          disabled={otp.length !== 6 || isSubmitting}
          sx={{
            mb: 2,
            py: 1.5,
            fontWeight: "bold",
            width: "100%",
          }}
        >
          {isSubmitting ? (
            <CircularProgress
              size={22}
              sx={{
                color: theme.palette.text.secondary,
              }}
            />
          ) : (
            t("otp_verify_button")
          )}
        </PrimaryButton>

        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          sx={{ mt: 2 }}
        >
          {timeLeft > 0 ? (
            <Typography>
              {t("resend_otp_in_seconds", { seconds: timeLeft })} {/* <--- Use new translation key with variable */}
            </Typography>
          ) : (
            <Box display="flex" alignItems="center">
              <Typography>
                {t("didnt_recieve_otp")}
              </Typography>
              <Button
                color="primary"
                variant="text"
                onClick={handleResend}
                size="small"
                sx={{ textTransform: "none", textDecoration: "underline" }}
              >
                {t("resend_otp")}
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

const blue = {
  100: "#DAECFF",
  200: "#80BFFF",
  400: "#3399FF",
  500: "#007FFF",
  600: "#0072E5",
  700: "#0059B2",
};

const grey = {
  50: "#F3F6F9",
  100: "#E5EAF2",
  200: "#DAE2ED",
  300: "#C7D0DD",
  400: "#B0B8C4",
  500: "#9DA8B7",
  600: "#6B7A90",
  700: "#434D5B",
  800: "#303740",
  900: "#1C2025",
};

const InputElement = styled("input")(
  ({ theme }) => `
  width: 60px;
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.5;
  padding: 16px 0;
  border-radius: 8px;
  text-align: center;
  color: ${theme.palette.mode === "dark" ? grey[300] : grey[900]};
  background: ${theme.palette.mode === "dark" ? grey[900] : "#fff"};
  border: 1px solid ${theme.palette.mode === "dark" ? grey[700] : grey[200]};


  &:hover {
    border-color: ${blue[400]};
  }

  &:focus {
    border-color: ${blue[400]};
    box-shadow: 0 0 0 3px ${
      theme.palette.mode === "dark" ? blue[600] : blue[200]
    };
  }

  /* firefox */
  &:focus-visible {
    outline: 0;
  }
`
);
